import { WATERMARK_PLACEMENTS, MAX_WATERMARK_CHARS, MIN_WATERMARK_SIZE,
         MAX_WATERMARK_SIZE, DEFAULT_WATERMARK_SIZE, WATERMARK_FONT_KEYS,
         DEFAULT_WATERMARK_FONT, fontSpec, MIN_LOGO_SIZE, MAX_LOGO_SIZE,
         DEFAULT_LOGO_SIZE } from "./watermark"
import { MEME_SLOTS, MEME_FONT_KEYS, DEFAULT_MEME_FONT, DEFAULT_MEME_COLOUR,
         MIN_MEME_SIZE, MAX_MEME_SIZE, DEFAULT_MEME_SIZE, MEME_OUTLINE_RATIO,
         normaliseCaption, captionMeasurer, layoutCaption, captionTops,
         captionCentre, usableMemeBox, memeFontSpec, memeOutline } from "./meme"

/* Flipping and region-cropping, done in the browser before the upload.
 *
 * ImageEngine cannot do either. flip_h and mirror_true both answer 412, and
 * cr_ works but refuses to combine with a resize -- five orderings of
 * /cr_.../w_600 all came back at the full 1600x1200. So the only place these
 * can happen is here, on a canvas, and the transformed file is what gets
 * uploaded.
 *
 * THE CANVAS IS CAPPED, and that is the whole safety story. iOS Safari
 * abandons canvas work above roughly 16.7 megapixels and returns a BLANK
 * canvas rather than an error -- the same silent failure that kept the
 * dropzone thumbnails empty. Two of the 112 real uploads recovered from the
 * bucket are already over that line (32.3 MP and 24.5 MP), and the largest
 * would need 129 MB as canvas RGBA.
 *
 * Capping at 2560px on the long edge removes the problem instead of managing
 * it. The app never emits anything wider than 1280 (_large; _small is 360, and
 * pct only scales down), so a 2560px canvas is 2x the largest thing we ship
 * and full-resolution work was never going to be seen. Worst real upload
 * becomes 2560x1923 -- 4.9 MP, 19.7 MB -- and nothing can reach the ceiling.
 * It also makes the re-encode moot: a 2560 -> 1280 downsample destroys any
 * artifact this introduces.
 *
 * Two rules the callers depend on:
 *
 * Nothing asked for means nothing done. planTransform().changed is false when
 * there is no crop and no flip, and transformImage() then returns the ORIGINAL
 * File. Anyone who does not touch the new controls uploads exactly the bytes
 * they picked, as before.
 *
 * The original's size is not ours to report. A transformed upload is not the
 * user's file any more, so the savings percentage has to keep measuring
 * against the file they chose. transformImage() carries it on .originalSize.
 */

/* The ceiling on a transformed image.
 *
 * This was 2560, which quietly cost a third of every phone photo: a 4032x3024
 * selfie came back 2560x1905, and ONLY when a flip or crop was asked for --
 * ask for nothing and the untouched original is uploaded at full size. So
 * "flip this" also meant "and shrink it", which nobody asked for.
 *
 * Raised to 4096 with an AREA cap beside it, because edge alone is not the
 * constraint. iOS Safari abandons a canvas above roughly 16.7 megapixels and
 * hands back a blank one instead of an error, and a 4096-wide limit on its own
 * lets a 4096x4096 square through at 16.8 MP, just past that.
 *
 * The area figure is 12.5 MP rather than the ceiling itself, so that the output
 * canvas never exceeds about 50 MB whatever shape arrives. That costs almost
 * nothing: for a wide photo the EDGE cap binds first anyway, so the two figures
 * only differ for something close to square. And an ordinary 12 MP phone photo
 * -- 4032x3024, the case this exists for -- passes through untouched.
 *
 * On memory: the source is decoded at its full size before any of this, so it
 * costs the same either way and is the larger half of the peak. Flipping a
 * 12 MP photo went from about 68 MB to about 98 MB, not from 20 to 50.
 *
 * BLANK_PROBE below still catches the failure if a device draws the line
 * somewhere lower than this. */
export const MAX_EDGE = 4096
export const MAX_AREA = 12500000

// Sampled to catch the blank canvas iOS hands back instead of an error. An
// opaque source cannot legitimately produce a fully transparent pixel, so
// alpha 0 everywhere means the draw was abandoned.
const BLANK_PROBE = 12

// Anything smaller than this is a stray drag rather than a crop, and is
// discarded in favour of the whole image.
const MIN_CROP_PX = 8

/* What a crop rectangle actually means against a particular picture.
 *
 * EXPORTED, because there are now two places that crop: this file draws the
 * region onto a canvas, and the store turns it into a `cr_` directive for an
 * animated GIF -- where a canvas would flatten the animation and where
 * re-encoding is expensive. Two copies of this arithmetic would be two answers
 * to "which pixels did they pick", so there is one.
 *
 * Intersect, do not clamp the corner and then measure from it. Pinning the
 * origin inside the image first turns a rectangle that misses the photo
 * entirely into a legal 1x1 crop, and the visitor gets a single pixel back
 * instead of their picture.
 *
 * Null means "no crop worth doing": absent, off the picture, or thinner than
 * MIN_CROP_PX on either side. A caller must treat null as the whole image
 * rather than as an error.
 */
export function cropRegion(crop, W, H) {
    if (!crop || !W || !H) return null
    const x0 = Math.max(Math.round(crop.x || 0), 0)
    const y0 = Math.max(Math.round(crop.y || 0), 0)
    const x1 = Math.min(Math.round((crop.x || 0) + (crop.w || 0)), W)
    const y1 = Math.min(Math.round((crop.y || 0) + (crop.h || 0)), H)
    const w = x1 - x0
    const h = y1 - y0
    if (w < MIN_CROP_PX || h < MIN_CROP_PX) return null
    return {x: x0, y: y0, w, h,
            cropped: x0 !== 0 || y0 !== 0 || w !== W || h !== H}
}

/* One crop rectangle, restated in the frame a quarter turn leaves behind.
 *
 * The rectangle is chosen against the UNTURNED picture -- CropSurface holds
 * its own turn at 0 while cropping, because turning the picture under the
 * rectangle would move the region out from under the pointer -- so its numbers
 * are in the original's coordinates. ImageEngine's cr_ reads them in the
 * TURNED ones, and it turns before it crops whichever order the directives are
 * written in (measured; the note in buildDirectives has the figures). Handing
 * the numbers over unchanged therefore cuts a different part of the picture.
 *
 * CLOCKWISE, which is what r_90 does -- rotating a plain response -90 with
 * Pillow matches it to a mean channel difference of 4.0, against 63.5 the
 * other way. So (x, y) becomes (H - y, x) for a quarter turn, and the
 * rectangle's own sides swap with it.
 *
 * Exported and shared rather than inlined in the store for the reason
 * cropRegion() already is: two answers to "which pixels did they pick" is two
 * answers.
 */
export function turnedRegion(region, W, H, rotate) {
    const deg = ((Math.round(rotate || 0) % 360) + 360) % 360
    if (!region || !W || !H || deg === 0) return region
    const {x, y, w, h} = region
    if (deg === 90) return {...region, x: H - y - h, y: x, w: h, h: w}
    if (deg === 180) return {...region, x: W - x - w, y: H - y - h, w, h}
    if (deg === 270) return {...region, x: y, y: W - x - w, w: h, h: w}
    // Not a quarter turn, so there is no rectangle that means the same thing.
    // The CDN only takes 90/180/270 anyway; anything else leaves it alone.
    return region
}

/* Pure geometry, so it can be tested without a DOM.
 *
 * crop is in SOURCE pixels and is INTERSECTED with the image; one that misses
 * it, or that comes back smaller than MIN_CROP_PX on either side, is discarded
 * in favour of the whole image rather than producing a sliver. */
/* A colour and an opacity, or null.
 *
 * Validated here rather than trusted, because a task page can preset it and
 * that page is a static file on a CDN. An opacity of zero, or out of range, or
 * a colour the canvas cannot parse, all mean "no overlay" -- which keeps the
 * untouched path untouched: transformImage() returns the ORIGINAL file when
 * nothing is asked for, and a scrim nobody can see must not cost a re-encode.
 */
export function usableOverlay(overlay) {
    if (!overlay || typeof overlay !== "object") return null
    const {color, opacity} = overlay
    if (typeof color !== "string" || !/^#[0-9a-f]{6}$/i.test(color.trim())) return null
    if (!Number.isFinite(opacity) || opacity <= 0 || opacity > 1) return null
    return {color: color.trim().toLowerCase(), opacity}
}

/* A watermark, validated the same way and for the same reason: a task page can
 * preset it, and that page is a static file on a CDN. Empty text, a placement
 * we do not draw, or an opacity of zero all mean "no watermark" -- which keeps
 * the untouched path untouched, because transformImage() returns the ORIGINAL
 * file when nothing is asked for and words nobody can see must not cost a
 * re-encode.
 */
export function usableWatermark(watermark) {
    if (!watermark || typeof watermark !== "object") return null
    const {text, position, size, opacity, color, font} = watermark
    if (typeof text !== "string") return null
    const words = text.trim().slice(0, MAX_WATERMARK_CHARS)
    if (!words) return null
    if (!WATERMARK_PLACEMENTS.includes(position)) return null
    if (!Number.isFinite(opacity) || opacity <= 0 || opacity > 1) return null
    if (typeof color !== "string" || !/^#[0-9a-f]{6}$/i.test(color.trim())) return null
    const fraction = Number.isFinite(size) ? size : DEFAULT_WATERMARK_SIZE
    return {
        text: words,
        position,
        opacity,
        color: color.trim().toLowerCase(),
        size: Math.min(MAX_WATERMARK_SIZE, Math.max(MIN_WATERMARK_SIZE, fraction)),
        // An unknown face is the same untrusted input as everything else a task
        // page can preset, and falling back to the default beats drawing
        // nothing.
        font: WATERMARK_FONT_KEYS.includes(font) ? font : DEFAULT_WATERMARK_FONT,
    }
}

/* Captions, validated like everything else that a task page can preset.
 *
 * Empty fields in EVERY area mean "no captions" -- which keeps the untouched
 * path untouched, because transformImage() returns the ORIGINAL file when
 * nothing is asked for and words nobody typed must not cost a re-encode.
 *
 * The text is normalised HERE rather than at the point of drawing, because
 * whether there is anything to draw is the same question as what the words
 * are once the whitespace is tidied away. normaliseCaption() is idempotent, so
 * layoutCaption() re-running it later changes nothing.
 */
export function usableMeme(meme) {
    if (!meme || typeof meme !== "object") return null
    const shout = meme.upper === undefined ? false : !!meme.upper

    /* PER AREA, keyed by slot, so both renderers can write meme[slot] and stop
     * choosing between named fields on every property.
     *
     * Colour and size belong to the area rather than to the picture: a caption
     * on a dark half of a photograph and one on a light half want different
     * colours, and a long line beside a short one wants a different size. The
     * face and the capitals stay shared -- two typefaces on one meme is a
     * different sort of decision.
     */
    const area = (text, color, size, box) => ({
        text: normaliseCaption(typeof text === "string" ? text : "", shout),
        color: typeof color === "string" && /^#[0-9a-f]{6}$/i.test(color.trim())
            ? color.trim().toLowerCase()
            : DEFAULT_MEME_COLOUR,
        size: Math.min(MAX_MEME_SIZE, Math.max(MIN_MEME_SIZE,
            Number.isFinite(size) ? size : DEFAULT_MEME_SIZE)),
        // Where the visitor dragged a rectangle, or where a library picture
        // said its own white space is. Null means the default band, and a
        // rectangle too small or off the picture is treated as null rather
        // than obeyed.
        box: usableMemeBox(box),
    })

    /* EVERY slot, so a label in area 3 is as real as a caption in area 1.
     *
     * Built by walking MEME_SLOTS rather than naming the areas, which is what
     * lets drawMeme() and WatermarkLayer.vue loop instead of choosing between
     * named fields on every property -- and what made going from two areas to
     * four a change to the LIST rather than to every renderer. */
    const out = {
        upper: shout,
        // An unknown face is the same untrusted input as everything else a
        // task page can preset, and falling back beats drawing nothing.
        font: MEME_FONT_KEYS.includes(meme.font) ? meme.font : DEFAULT_MEME_FONT,
    }
    let any = false
    for (const slot of MEME_SLOTS) {
        out[slot] = area(meme[slot], meme[`${slot}Color`],
                         meme[`${slot}Size`], meme[`${slot}Box`])
        if (out[slot].text) any = true
    }
    // No words in any area is not a caption, which keeps the untouched path
    // untouched: transformImage() returns the ORIGINAL file when nothing is
    // asked for, and empty fields must not cost a re-encode.
    if (!any) return null
    return out
}

/* Black outline under light text, white under dark, so the words survive
 * whatever they land on. Rec. 709 luma, which is close enough for a decision
 * with two outcomes. */
function outlineFor(hex) {
    const n = parseInt(hex.slice(1), 16)
    const luma = 0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)
    return luma > 140 ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.5)"
}

/* Drawn after the picture and after any scrim, in the SAME pass.
 *
 * The font is a stack the browser already has, deliberately. Canvas fillText()
 * does not wait for a webfont: ask for one that has not finished loading and it
 * silently draws in something else, which is invisible in a snapshot and wrong
 * in the file the visitor downloads.
 *
 * Size is a fraction of the SHORT edge, so the same setting reads the same on a
 * portrait and a landscape, and on a thumbnail and a six-thousand-pixel photo.
 */
/* A logo, validated like everything else that reaches the canvas.
 *
 * The image must already be DECODED -- naturalWidth is the proof. An <img>
 * that has not loaded draws nothing at all, silently, which would leave a
 * visitor with an unmarked picture and no error to explain it.
 */
export function usableLogo(logo) {
    if (!logo || typeof logo !== "object") return null
    const {image, position, size, opacity} = logo
    if (!image) return null
    const w = image.naturalWidth || image.width || 0
    const h = image.naturalHeight || image.height || 0
    if (!w || !h) return null
    if (!WATERMARK_PLACEMENTS.includes(position)) return null
    if (!Number.isFinite(opacity) || opacity <= 0 || opacity > 1) return null
    const fraction = Number.isFinite(size) ? size : DEFAULT_LOGO_SIZE
    return {
        image, width: w, height: h, position, opacity,
        size: Math.min(MAX_LOGO_SIZE, Math.max(MIN_LOGO_SIZE, fraction)),
    }
}

/* Drawn like the lettering, and after it for the same reason: a logo under
 * the scrim would be dimmed by it.
 *
 * The logo is fitted INTO a box whose side is a fraction of the short edge,
 * keeping its own proportions. Scaling to the box would squash a wide logo on
 * a tall photograph, which is the one thing nobody would accept of their own
 * mark.
 */
function drawLogo(ctx, lg, dw, dh) {
    const short = Math.min(dw, dh)
    const box = Math.max(8, short * lg.size)
    const scale = Math.min(box / lg.width, box / lg.height)
    const w = Math.round(lg.width * scale)
    const h = Math.round(lg.height * scale)
    const margin = Math.round(short * 0.04)

    ctx.save()
    ctx.globalAlpha = lg.opacity
    const stamp = (x, y) => ctx.drawImage(lg.image, Math.round(x), Math.round(y), w, h)

    if (lg.position === "tiled") {
        const stepX = w + short * 0.06
        const stepY = h + short * 0.06
        const reach = Math.ceil(Math.hypot(dw, dh))
        ctx.translate(dw / 2, dh / 2)
        ctx.rotate(-Math.PI / 6)
        for (let y = -reach; y <= reach; y += stepY) {
            for (let x = -reach; x <= reach; x += stepX) stamp(x - w / 2, y - h / 2)
        }
        ctx.restore()
        return
    }
    if (lg.position === "diagonal") {
        ctx.translate(dw / 2, dh / 2)
        ctx.rotate(-Math.atan2(dh, dw))
        stamp(-w / 2, -h / 2)
        ctx.restore()
        return
    }
    if (lg.position === "centre") {
        stamp((dw - w) / 2, (dh - h) / 2)
        ctx.restore()
        return
    }
    const x = lg.position.endsWith("right") ? dw - margin - w : margin
    const y = lg.position.startsWith("bottom") ? dh - margin - h : margin
    stamp(x, y)
    ctx.restore()
}

/* Wait for the face before drawing, or do not draw it at all.
 *
 * canvas fillText() does not block on a webfont: ask for one that has not
 * arrived and it silently draws in something else, which looks fine on screen
 * and is wrong in the file the visitor downloads. Only Lato needs this today,
 * but the wait is cheap and asking for a face the device already has resolves
 * immediately.
 *
 * A face that cannot be loaded is a fallback, not a failure: the stack ends in
 * a generic family and the drawing goes ahead.
 */
export async function ensureWatermarkFont(watermark) {
    const wm = usableWatermark(watermark)
    if (!wm) return
    if (typeof document === "undefined" || !document.fonts || !document.fonts.load) return
    try {
        await document.fonts.load(fontSpec(24, wm.font))
    } catch (e) {
        /* falls back */
    }
}

/* The same wait, and it matters more here.
 *
 * A watermark's default face is one the operating system already has, so the
 * wait usually resolves instantly and the hazard is theoretical. A caption's
 * default is Anton, a webfont, chosen precisely BECAUSE Impact is missing from
 * Android, iOS and most Linux. So the face is nearly always one that has to
 * arrive over the network, and drawing before it does would put the visitor's
 * caption in whatever the fallback happens to be -- correct on screen, wrong
 * in the file, and with nothing to indicate it.
 */
export async function ensureMemeFont(meme) {
    const mm = usableMeme(meme)
    if (!mm) return
    if (typeof document === "undefined" || !document.fonts || !document.fonts.load) return
    try {
        await document.fonts.load(memeFontSpec(24, mm.font))
    } catch (e) {
        /* falls back */
    }
}

function drawWatermark(ctx, wm, dw, dh) {
    const px = Math.max(9, Math.round(Math.min(dw, dh) * wm.size))
    ctx.save()
    ctx.globalAlpha = wm.opacity
    ctx.fillStyle = wm.color
    ctx.strokeStyle = outlineFor(wm.color)
    ctx.lineWidth = Math.max(1, px / 12)
    ctx.lineJoin = "round"
    ctx.font = fontSpec(px, wm.font)
    ctx.textBaseline = "alphabetic"

    const margin = Math.round(Math.min(dw, dh) * 0.04)
    const stamp = (x, y) => { ctx.strokeText(wm.text, x, y); ctx.fillText(wm.text, x, y) }

    if (wm.position === "tiled") {
        // Rotated so the repeat does not line up with the subject, and stepped
        // by the measured width so long text does not overlap itself.
        const w = ctx.measureText(wm.text).width
        const stepX = w + px * 1.6
        const stepY = px * 3.2
        const reach = Math.ceil(Math.hypot(dw, dh))
        ctx.translate(dw / 2, dh / 2)
        ctx.rotate(-Math.PI / 6)
        ctx.textAlign = "center"
        for (let y = -reach; y <= reach; y += stepY) {
            for (let x = -reach; x <= reach; x += stepX) stamp(x, y)
        }
        ctx.restore()
        return
    }

    if (wm.position === "diagonal") {
        // Along the picture's own diagonal, so it reads corner to corner
        // whatever the shape of the image.
        ctx.translate(dw / 2, dh / 2)
        ctx.rotate(-Math.atan2(dh, dw))
        ctx.textAlign = "center"
        stamp(0, px / 3)
        ctx.restore()
        return
    }

    if (wm.position === "centre") {
        ctx.textAlign = "center"
        stamp(dw / 2, dh / 2 + px / 3)
        ctx.restore()
        return
    }

    const right = wm.position.endsWith("right")
    const bottom = wm.position.startsWith("bottom")
    ctx.textAlign = right ? "right" : "left"
    stamp(right ? dw - margin : margin, bottom ? dh - margin : margin + px)
    ctx.restore()
}

/* Captions, drawn in the same pass and after everything else.
 *
 * WHERE THE LINES BREAK IS NOT DECIDED HERE. lib/meme.js decides, and
 * WatermarkLayer.vue asks it the same question to draw the live preview. This
 * function only multiplies the answer by the short edge and puts the letters
 * down. That division of labour is the whole design: a watermark is one line
 * at one size and survives being implemented twice, a wrapping caption does
 * not, and a preview that breaks a line somewhere the download does not is a
 * lie the visitor only discovers after saving the file.
 */
/* EXPORTED so the video path can bake a caption onto each frame with the same
 * renderer the meme page uses. One renderer, one layout function, one set of
 * outline rules -- writing a second caption drawer for GIF frames is exactly
 * the divergence lib/meme.js exists to prevent. */
export function drawMeme(ctx, meme, dw, dh) {
    const short = Math.min(dw, dh)
    const aspect = dw / dh
    // Built before any drawing font is set, and it re-asserts its own font on
    // every measurement, so the sizes set below cannot corrupt the layout.
    const measure = captionMeasurer(ctx, meme.font)

    ctx.save()
    ctx.lineJoin = "round"
    // Without this, a sharp join on a heavy face throws long spikes off the
    // corners of letters like M and W at these stroke widths.
    ctx.miterLimit = 2
    ctx.textAlign = "center"
    ctx.textBaseline = "top"

    for (const slot of MEME_SLOTS) {
        const area = meme[slot]
        const layout = layoutCaption({
            text: area.text, aspect, measure,
            size: area.size, upper: meme.upper, box: area.box, slot,
        })
        if (!layout) continue

        // Inside the loop now that each area carries its own. Setting these
        // once above the loop is what would make the second caption silently
        // borrow the first one's colour.
        ctx.fillStyle = area.color
        ctx.strokeStyle = memeOutline(area.color)
        const px = layout.size * short
        ctx.font = memeFontSpec(px, meme.font)
        ctx.lineWidth = Math.max(1, px * MEME_OUTLINE_RATIO)
        // Half-leading, the way CSS distributes it, so that a line box in the
        // preview and a baseline here describe the same band of the picture.
        const lead = (layout.lineHeight - layout.size) * short / 2
        const tops = captionTops(slot, layout)
        // The middle of the rectangle, which is the middle of the picture until
        // somebody drags one somewhere else.
        const centre = captionCentre(layout, aspect) * short

        layout.lines.forEach((line, i) => {
            const y = tops[i] * short + lead
            // Stroke first, then fill: the keyline is drawn centred on the
            // glyph outline, so filling afterwards keeps the letter its proper
            // weight instead of letting the outline eat half of it.
            ctx.strokeText(line, centre, y)
            ctx.fillText(line, centre, y)
        })
    }
    ctx.restore()
}

export function planTransform({width, height, crop = null, flipH = false, flipV = false,
                               overlay = null, watermark = null, logo = null,
                               meme = null,
                               maxEdge = MAX_EDGE, maxArea = MAX_AREA}) {
    const W = Math.max(0, Math.floor(width || 0))
    const H = Math.max(0, Math.floor(height || 0))
    if (!W || !H) {
        return {ok: false, reason: "no source dimensions"}
    }

    let sx = 0, sy = 0, sw = W, sh = H, cropped = false
    const region = cropRegion(crop, W, H)
    if (region) {
        sx = region.x; sy = region.y; sw = region.w; sh = region.h
        cropped = region.cropped
    }

    // Whichever ceiling binds first. Both shrink the two edges together, so
    // the picture keeps its shape either way.
    const byEdge = maxEdge / Math.max(sw, sh)
    const byArea = Math.sqrt(maxArea / (sw * sh))
    const scale = Math.min(1, byEdge, byArea)
    // Rounded DOWN when anything is being shrunk, so a ceiling is a ceiling:
    // rounding to nearest can land just past it -- a 4096 square scaled to the
    // area cap comes out 3536x3536, which is 12,503,296 against a limit of
    // 12,500,000. Rounded up at scale 1 there is nothing to round.
    const round = scale < 1 ? Math.floor : Math.round
    const dw = Math.max(1, round(sw * scale))
    const dh = Math.max(1, round(sh * scale))

    return {
        ok: true,
        sx, sy, sw, sh, dw, dh,
        scale,
        cropped,
        flipH: !!flipH,
        flipV: !!flipV,
        // A downscale on its own is not a reason to re-encode someone's photo:
        // ImageEngine does that better, from the original.
        // A scrim at zero opacity is not a scrim, and re-encoding for it
        // would cost a generation of quality for a picture nobody changed.
        overlay: usableOverlay(overlay),
        watermark: usableWatermark(watermark),
        logo: usableLogo(logo),
        // Two empty caption fields are not a caption, for the same reason a
        // scrim at zero opacity is not a scrim.
        meme: usableMeme(meme),
        changed: cropped || !!flipH || !!flipV || !!usableOverlay(overlay)
                 || !!usableWatermark(watermark) || !!usableLogo(logo)
                 || !!usableMeme(meme),
    }
}

/* Suffix and MIME for the re-encode.
 *
 * PNG stays PNG so a transparent source keeps its alpha; everything else
 * becomes JPEG, which is what the cap makes safe. */
export function outputType(sourceType) {
    return String(sourceType || "").toLowerCase() === "image/png"
        ? {mime: "image/png", ext: "png", quality: undefined}
        : {mime: "image/jpeg", ext: "jpg", quality: 0.92}
}

function renameFor(name, ext) {
    const base = String(name || "image").replace(/\.[^.]+$/, "")
    return `${base}.${ext}`
}

function looksBlank(ctx, dw, dh) {
    let data
    try {
        data = ctx.getImageData(0, 0, Math.min(dw, BLANK_PROBE), Math.min(dh, BLANK_PROBE)).data
    } catch (err) {
        return false      // a tainted or unreadable canvas is not evidence of a blank one
    }
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] !== 0) return false
    }
    return true
}

function loadImage(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const img = new Image()
        const done = () => resolve({img, release: () => URL.revokeObjectURL(url)})
        // <img> applies EXIF orientation on its own -- image-orientation:
        // from-image is the initial value -- and naturalWidth/Height come back
        // already oriented. createImageBitmap does not agree across browsers
        // about whether it should, which is how a flip turns into a rotation.
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("the browser could not decode this image")) }
        if (img.decode) {
            img.src = url
            img.decode().then(done).catch(() => { img.onload = done })
        } else {
            img.onload = done
            img.src = url
        }
    })
}

function toBlob(canvas, mime, quality) {
    return new Promise((resolve, reject) => {
        if (canvas.toBlob) {
            canvas.toBlob((b) => b ? resolve(b) : reject(new Error("the browser could not encode the result")), mime, quality)
        } else {
            try {
                const parts = canvas.toDataURL(mime, quality).split(",")
                const bin = atob(parts[1])
                const bytes = new Uint8Array(bin.length)
                for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
                resolve(new Blob([bytes], {type: mime}))
            } catch (err) {
                reject(new Error("the browser could not encode the result"))
            }
        }
    })
}

/* Returns a File. The ORIGINAL File when nothing was asked for, so the
 * untouched path is byte-for-byte what it always was.
 *
 * Throws with something sayable out loud. The caller has to show it: a silent
 * failure here is a photo the user believes was flipped and was not. */
export async function transformImage(file, {crop = null, flipH = false, flipV = false,
                                            overlay = null, watermark = null,
                                            logo = null, meme = null,
                                            maxEdge = MAX_EDGE} = {}) {
    if (!crop && !flipH && !flipV && !usableOverlay(overlay) && !usableWatermark(watermark)
            && !usableLogo(logo) && !usableMeme(meme)) {
        return file
    }

    // Before the canvas exists, so the face is ready by the time anything is
    // drawn with it.
    await ensureWatermarkFont(watermark)
    await ensureMemeFont(meme)

    const {img, release} = await loadImage(file)
    let canvas = null
    try {
        const plan = planTransform({
            width: img.naturalWidth || img.width,
            height: img.naturalHeight || img.height,
            crop, flipH, flipV, overlay, watermark, logo, meme, maxEdge,
        })
        if (!plan.ok) throw new Error("the browser could not measure this image")
        if (!plan.changed) return file

        canvas = document.createElement("canvas")
        canvas.width = plan.dw
        canvas.height = plan.dh
        const ctx = canvas.getContext("2d")
        if (!ctx) throw new Error("this browser has no canvas to work on")

        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = "high"
        ctx.translate(plan.flipH ? plan.dw : 0, plan.flipV ? plan.dh : 0)
        ctx.scale(plan.flipH ? -1 : 1, plan.flipV ? -1 : 1)
        ctx.drawImage(img, plan.sx, plan.sy, plan.sw, plan.sh, 0, 0, plan.dw, plan.dh)
        ctx.setTransform(1, 0, 0, 1, 0, 0)

        // The scrim, in the SAME pass. A second pass would decode and re-encode
        // the picture twice for one flat rectangle, and on iOS it would meet
        // the canvas ceiling twice as well.
        //
        // source-atop rather than a plain fill, so transparency survives: a
        // logo on a transparent PNG gets tinted where the logo is and stays
        // clear everywhere else. A flat fillRect would turn the empty half of
        // that file into a solid block of colour.
        if (plan.overlay) {
            ctx.save()
            ctx.globalCompositeOperation = "source-atop"
            ctx.globalAlpha = plan.overlay.opacity
            ctx.fillStyle = plan.overlay.color
            ctx.fillRect(0, 0, plan.dw, plan.dh)
            ctx.restore()
        }

        // After the scrim on purpose: a watermark laid under the darkening
        // would be dimmed by it, and the point of both together is readable
        // words on a darkened picture.
        if (plan.watermark) {
            drawWatermark(ctx, plan.watermark, plan.dw, plan.dh)
        }
        if (plan.logo) {
            drawLogo(ctx, plan.logo, plan.dw, plan.dh)
        }
        // Last, and above everything: a caption that a scrim or a watermark
        // could sit on top of would be the one thing on the picture that is
        // meant to be read and is not.
        if (plan.meme) {
            drawMeme(ctx, plan.meme, plan.dw, plan.dh)
        }

        if (looksBlank(ctx, plan.dw, plan.dh)) {
            throw new Error("this image is too large for your browser to work on")
        }

        const {mime, ext, quality} = outputType(file.type)
        const blob = await toBlob(canvas, mime, quality)
        const out = new File([blob], renameFor(file.name, ext), {type: mime, lastModified: Date.now()})
        // The savings percentage still has to measure against what the user
        // picked, not against our re-encode of it.
        out.originalSize = file.originalSize || file.size
        return out
    } finally {
        release()
        // Not merely dropping the reference: a batch of ten wants each canvas
        // handed back before the next one is built.
        if (canvas) { canvas.width = 0; canvas.height = 0 }
    }
}
